import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, label, btnPrimary, btnSecondary } from '../styles/theme.js'
import { tasks as tasksApi, sessions as sessionsApi, projects as projectsApi, tags as tagsApi, rubrics as rubricsApi } from '../lib/api.js'

// --- Styles ---

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.lg};
`

const taskGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${space.md};
  margin-bottom: ${space.xl};
`

const taskCard = css`
  ${card};
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: ${colors.brand}; }
`

const taskHeader = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  margin-bottom: ${space.sm};
`

const taskTitle = css`
  font-weight: ${font.weight.semibold};
  flex: 1;
`

const statusBadge = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  padding: 2px 8px;
  border-radius: 10px;
`

const typeBadge = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  padding: 2px 8px;
  border-radius: 10px;
  background: ${colors.bg};
  color: ${colors.textSecondary};
`

const taskDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${space.sm};
  line-height: 1.5;
`

const taskMeta = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  display: flex;
  gap: ${space.md};
`

const progressBar = css`
  height: 4px;
  background: ${colors.border};
  border-radius: 2px;
  margin-top: ${space.sm};
  overflow: hidden;
`

const progressFill = css`
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

// --- Modal styles ---

const formOverlay = css`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const formCard = css`
  ${card};
  width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`

const formTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.lg};
`

const formRow = css`
  margin-bottom: ${space.md};
`

const formActions = css`
  display: flex;
  gap: ${space.sm};
  justify-content: flex-end;
  margin-top: ${space.lg};
`

const filterRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  flex-wrap: wrap;
  margin-bottom: ${space.sm};
`

const filterLabel = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-weight: ${font.weight.medium};
  white-space: nowrap;
`

const filterInput = css`
  padding: 4px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  width: 130px;
  &:focus { border-color: ${colors.borderFocus}; outline: none; }
`

const filterSelect = css`
  padding: 4px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
`

const filterDivider = css`
  width: 1px;
  height: 18px;
  background: ${colors.border};
`

const tagChipStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  user-select: none;
  &:hover { opacity: 0.8; }
`

const tagDropdownWrap = css`
  position: relative;
  display: inline-block;
`

const tagDropdownBtn = css`
  padding: 4px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${colors.bg}; }
`

const tagDropdownPanel = css`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  width: 240px;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 60;
  overflow: hidden;
`

const tagDropdownSearch = css`
  width: 100%;
  padding: ${space.sm} ${space.md};
  border: none;
  border-bottom: 1px solid ${colors.border};
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: transparent;
  color: ${colors.text};
  outline: none;
  box-sizing: border-box;
  &::placeholder { color: ${colors.textMuted}; }
`

const tagDropdownList = css`
  max-height: 200px;
  overflow-y: auto;
`

const tagDropdownItem = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: 6px ${space.md};
  cursor: pointer;
  font-size: ${font.size.sm};
  &:hover { background: ${colors.bg}; }
`

const tagSwatch = css`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
`

const matchCount = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.semibold};
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  background: ${colors.bg};
  text-align: center;
`

const rubricBadge = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-left: ${space.xs};
`

// --- Helpers ---

export function statusColor(status) {
  return status === 'open'
    ? colors.green
    : colors.textMuted
}

export function typeLabel(type) {
  const map = { tagging: 'Tagging', evaluation: 'Evaluation', review: 'Review' }
  return map[type] || type
}

export function progressPercent(task) {
  if (!task.total || task.total === 0) return 0
  return Math.round((task.completed / task.total) * 100)
}

// --- Component ---

export function TasksView() {
  let projectId = null
  let project = null
  let taskList = []
  let loading = true
  let showForm = false
  let saving = false

  // Form state
  let formTitle = ''
  let formDescription = ''
  let formType = 'evaluation'
  let formMaskPii = false
  let formRubricId = ''

  // Filter state for session selection
  let filterSearch = ''
  let filterTagIds = new Set()
  let filterExcludeTagIds = new Set()
  let filterTagMode = 'any'
  let filterUntagged = false
  let filterDateFrom = ''
  let filterDateTo = ''
  let filterMinMessages = ''
  let filterMaxMessages = ''
  let filterEvalStatus = 'any'  // 'any' | 'evaluated' | 'unevaluated'
  let filterEvalRubricId = ''

  // Tag dropdown state
  let tagDropdownOpen = false
  let tagSearchText = ''

  // Preview count
  let previewCount = null
  let previewLoading = false
  let previewDebounce = null

  // Available tags and rubrics
  let allTags = []
  let rubricList = []

  async function loadTasks(pid) {
    loading = true
    try {
      taskList = await tasksApi.list(pid)
    } catch (err) {
      console.error('Failed to load tasks:', err)
      taskList = []
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function loadTags(pid) {
    try { allTags = await tagsApi.list(pid) } catch { allTags = [] }
    m.redraw()
  }

  async function loadRubrics(pid) {
    try { rubricList = await rubricsApi.list(pid) } catch { rubricList = [] }
    m.redraw()
  }

  function buildFilterParams() {
    const params = { limit: 1 }
    if (filterSearch.trim()) params.search = filterSearch.trim()
    if (filterTagIds.size > 0) {
      params.tagIds = [...filterTagIds].join(',')
      params.tagMode = filterTagMode
    }
    if (filterExcludeTagIds.size > 0) {
      params.excludeTagIds = [...filterExcludeTagIds].join(',')
    }
    if (filterUntagged) params.untagged = '1'
    if (filterDateFrom) params.dateFrom = filterDateFrom
    if (filterDateTo) params.dateTo = filterDateTo + 'T23:59:59'
    if (filterMinMessages !== '' && !isNaN(parseInt(filterMinMessages, 10))) params.minMessages = parseInt(filterMinMessages, 10)
    if (filterMaxMessages !== '' && !isNaN(parseInt(filterMaxMessages, 10))) params.maxMessages = parseInt(filterMaxMessages, 10)
    if (filterEvalStatus === 'evaluated') params.evaluated = 'yes'
    else if (filterEvalStatus === 'unevaluated') params.evaluated = 'no'
    if (filterEvalRubricId) params.evalRubricId = filterEvalRubricId
    return params
  }

  function buildFilterBody() {
    const f = {}
    if (filterSearch.trim()) f.search = filterSearch.trim()
    if (filterTagIds.size > 0) {
      f.tagIds = [...filterTagIds]
      f.tagMode = filterTagMode
    }
    if (filterExcludeTagIds.size > 0) f.excludeTagIds = [...filterExcludeTagIds]
    if (filterUntagged) f.untagged = true
    if (filterDateFrom) f.dateFrom = filterDateFrom
    if (filterDateTo) f.dateTo = filterDateTo + 'T23:59:59'
    if (filterMinMessages !== '' && !isNaN(parseInt(filterMinMessages, 10))) f.minMessages = parseInt(filterMinMessages, 10)
    if (filterMaxMessages !== '' && !isNaN(parseInt(filterMaxMessages, 10))) f.maxMessages = parseInt(filterMaxMessages, 10)
    if (filterEvalStatus === 'evaluated') f.evaluated = 'yes'
    else if (filterEvalStatus === 'unevaluated') f.evaluated = 'no'
    if (filterEvalRubricId) f.evalRubricId = filterEvalRubricId
    return f
  }

  function hasActiveFilters() {
    return filterSearch.trim() || filterTagIds.size > 0 || filterExcludeTagIds.size > 0 ||
      filterUntagged || filterDateFrom || filterDateTo ||
      filterMinMessages !== '' || filterMaxMessages !== '' ||
      filterEvalStatus !== 'any'
  }

  async function updatePreviewCount() {
    clearTimeout(previewDebounce)
    previewDebounce = setTimeout(async () => {
      previewLoading = true
      m.redraw()
      try {
        const params = buildFilterParams()
        const result = await sessionsApi.list(projectId, params)
        previewCount = result.total
      } catch {
        previewCount = null
      } finally {
        previewLoading = false
        m.redraw()
      }
    }, 300)
  }

  function onFilterChange() {
    updatePreviewCount()
  }

  function openCreate() {
    formTitle = ''
    formDescription = ''
    formType = 'evaluation'
    formMaskPii = false
    formRubricId = ''
    filterSearch = ''
    filterTagIds = new Set()
    filterExcludeTagIds = new Set()
    filterTagMode = 'any'
    filterUntagged = false
    filterDateFrom = ''
    filterDateTo = ''
    filterMinMessages = ''
    filterMaxMessages = ''
    filterEvalStatus = 'any'
    filterEvalRubricId = ''
    tagDropdownOpen = false
    tagSearchText = ''
    previewCount = null
    previewLoading = false
    showForm = true
    // Load total session count (no filters) as initial preview
    updatePreviewCount()
    if (allTags.length === 0) loadTags(projectId)
    if (rubricList.length === 0) loadRubrics(projectId)
  }

  function closeForm() {
    showForm = false
    saving = false
    clearTimeout(previewDebounce)
  }

  function cycleTag(tagId) {
    if (filterTagIds.has(tagId)) {
      filterTagIds.delete(tagId)
      filterExcludeTagIds.add(tagId)
    } else if (filterExcludeTagIds.has(tagId)) {
      filterExcludeTagIds.delete(tagId)
    } else {
      filterTagIds.add(tagId)
    }
    filterUntagged = false
    onFilterChange()
  }

  function removeTagFilter(tagId) {
    filterTagIds.delete(tagId)
    filterExcludeTagIds.delete(tagId)
    onFilterChange()
  }

  function getTagState(tagId) {
    if (filterTagIds.has(tagId)) return 'include'
    if (filterExcludeTagIds.has(tagId)) return 'exclude'
    return 'off'
  }

  async function submitForm() {
    if (!formTitle.trim()) return
    if (!hasActiveFilters() && previewCount === null) return
    saving = true
    m.redraw()
    try {
      const data = {
        title: formTitle.trim(),
        description: formDescription.trim() || undefined,
        type: formType,
        maskPii: formMaskPii,
      }
      if (hasActiveFilters()) {
        data.filter = buildFilterBody()
      } else {
        // No filters = all sessions. Send filter with empty object so backend resolves all.
        data.filter = {}
      }
      if (formType === 'evaluation' && formRubricId) {
        data.rubricId = formRubricId
      }
      await tasksApi.create(projectId, data)
      closeForm()
      await loadTasks(projectId)
    } catch (err) {
      console.error('Failed to create task:', err)
      saving = false
      m.redraw()
    }
  }

  function filteredDropdownTags() {
    if (!tagSearchText.trim()) return allTags
    const q = tagSearchText.toLowerCase()
    return allTags.filter(t => t.name.toLowerCase().includes(q))
  }

  function selectedTags() {
    return allTags.filter(t => filterTagIds.has(t.id) || filterExcludeTagIds.has(t.id))
  }

  function onDocClick() {
    if (tagDropdownOpen) {
      tagDropdownOpen = false
      m.redraw()
    }
  }

  function renderTagFilter() {
    const selected = selectedTags()
    const hasActive = selected.length > 0

    return m('div', { class: tagDropdownWrap }, [
      m('button', {
        class: tagDropdownBtn,
        style: hasActive ? `border-color: ${colors.brand};` : '',
        onclick: (e) => {
          e.stopPropagation()
          tagDropdownOpen = !tagDropdownOpen
          tagSearchText = ''
        },
      }, [
        'Tags',
        hasActive
          ? m('span', {
              style: `background: ${colors.brand}; color: white; font-size: 10px; font-weight: ${font.weight.semibold}; border-radius: 50%; width: 16px; height: 16px; display: inline-flex; align-items: center; justify-content: center;`,
            }, selected.length)
          : null,
      ]),
      tagDropdownOpen
        ? m('div', {
            class: tagDropdownPanel,
            onclick: (e) => e.stopPropagation(),
          }, [
            m('input', {
              class: tagDropdownSearch,
              type: 'text',
              placeholder: 'Search tags...',
              value: tagSearchText,
              oninput: (e) => { tagSearchText = e.target.value },
              oncreate: (vnode) => vnode.dom.focus(),
              onkeydown: (e) => {
                if (e.key === 'Escape') { tagDropdownOpen = false; e.stopPropagation() }
              },
            }),
            m('div', { class: tagDropdownList }, [
              m('div', {
                key: 'untagged',
                class: tagDropdownItem,
                onclick: () => {
                  filterUntagged = !filterUntagged
                  if (filterUntagged) {
                    filterTagIds = new Set()
                    filterExcludeTagIds = new Set()
                  }
                  onFilterChange()
                },
              }, [
                m('div', { class: tagSwatch, style: `background: ${colors.textMuted};` }),
                m('span', { style: 'flex: 1;' }, 'Untagged'),
                filterUntagged
                  ? m('span', {
                      style: `background: ${colors.brand}; color: white; font-size: 11px; font-weight: ${font.weight.semibold}; width: 18px; height: 18px; border-radius: 4px; display: flex; align-items: center; justify-content: center;`,
                    }, '\u2713')
                  : null,
              ]),
              ...filteredDropdownTags().map(t => {
                const state = getTagState(t.id)
                return m('div', {
                  key: t.id,
                  class: tagDropdownItem,
                  onclick: () => cycleTag(t.id),
                }, [
                  m('div', { class: tagSwatch, style: `background: ${t.color || colors.border};` }),
                  m('span', { style: 'flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;' }, t.name),
                  state === 'include'
                    ? m('span', {
                        style: `background: #a9e34b30; color: #5a8a00; font-size: 11px; font-weight: ${font.weight.semibold}; width: 18px; height: 18px; border-radius: 4px; display: flex; align-items: center; justify-content: center;`,
                      }, '+')
                    : state === 'exclude'
                      ? m('span', {
                          style: `background: ${colors.critical}20; color: ${colors.critical}; font-size: 11px; font-weight: ${font.weight.semibold}; width: 18px; height: 18px; border-radius: 4px; display: flex; align-items: center; justify-content: center;`,
                        }, '\u2212')
                      : null,
                ])
              }),
            ]),
          ])
        : null,
    ])
  }

  function renderSelectedTagChips() {
    const selected = selectedTags()
    if (selected.length === 0 && !filterUntagged) return null

    const chips = []
    if (filterUntagged) {
      chips.push(m('span', {
        key: 'untagged',
        class: tagChipStyle,
        style: `background: ${colors.textMuted}30; color: ${colors.text}; border: 1px solid ${colors.textMuted};`,
        onclick: () => { filterUntagged = false; onFilterChange() },
      }, ['Untagged', m('span', { style: 'margin-left: 2px; opacity: 0.6;' }, '\u00d7')]))
    }
    for (const t of selected) {
      const isExclude = getTagState(t.id) === 'exclude'
      chips.push(m('span', {
        key: t.id,
        class: tagChipStyle,
        style: isExclude
          ? `background: ${colors.critical}15; color: ${colors.critical}; border: 1px solid ${colors.critical}40; text-decoration: line-through;`
          : `background: ${t.color || colors.border}30; color: ${colors.text}; border: 1px solid ${t.color || colors.border};`,
        onclick: () => removeTagFilter(t.id),
      }, [isExclude ? '\u2212 ' : '', t.name, m('span', { style: 'margin-left: 2px; opacity: 0.6;' }, '\u00d7')]))
    }
    if (filterTagIds.size > 1) {
      chips.push(m('button', {
        key: 'mode-toggle',
        style: `font-size: ${font.size.xs}; color: ${colors.textMuted}; cursor: pointer; padding: 2px 6px; border-radius: 4px; border: 1px solid ${colors.border}; background: ${colors.bgCard};`,
        onclick: () => { filterTagMode = filterTagMode === 'any' ? 'all' : 'any'; onFilterChange() },
      }, filterTagMode === 'any' ? 'Any' : 'All'))
    }
    return m('div', { style: `display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: ${space.sm};` }, chips)
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      projectsApi.get(projectId).then(p => { project = p; m.redraw() }).catch(() => {})
      loadTasks(projectId)
    },

    oncreate() {
      document.addEventListener('click', onDocClick)
    },

    onremove() {
      document.removeEventListener('click', onDocClick)
      clearTimeout(previewDebounce)
    },

    view(vnode) {
      projectId = vnode.attrs.projectId

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project?.name || 'Project'),
          ' / ',
          'Tasks',
        ]),

        m('div', {
          style: `display: flex; justify-content: space-between; align-items: center; margin-bottom: ${space.lg};`,
        }, [
          m('h1', { class: pageTitle, style: 'margin-bottom: 0;' }, 'Tasks'),
          m('button', { class: btnPrimary, onclick: openCreate }, 'New task'),
        ]),

        loading
          ? m('div', { class: emptyState }, 'Loading...')
          : taskList.length === 0
            ? m('div', { class: emptyState }, [
                m('p', 'No tasks yet.'),
                m('p', { style: `margin-top: ${space.sm};` },
                  'Create tasks to assign session review work.'),
              ])
            : m('div', { class: taskGrid },
                taskList.map(task => {
                  const children = []
                  children.push(m('div', { key: 'header', class: taskHeader }, [
                    m('div', { class: taskTitle }, task.title),
                    m('span', {
                      class: statusBadge,
                      style: `background: ${statusColor(task.status)}22; color: ${statusColor(task.status)};`,
                    }, task.status),
                  ]))
                  children.push(m('span', { key: 'type', class: typeBadge }, [
                    typeLabel(task.type),
                    task.rubric_id
                      ? m('span', { class: rubricBadge }, ' \u2022 Rubric')
                      : null,
                  ]))
                  if (task.description) {
                    children.push(m('div', { key: 'desc', class: taskDesc, style: `margin-top: ${space.sm};` }, task.description))
                  }
                  children.push(m('div', { key: 'meta', class: taskMeta }, [
                    m('span', `${task.total || 0} sessions`),
                    m('span', `${task.completed || 0} done`),
                    task.invite_count !== undefined
                      ? m('span', `${task.invite_count} invites`)
                      : null,
                  ]))
                  if (task.total > 0) {
                    children.push(m('div', { key: 'progress', class: progressBar }, [
                      m('div', {
                        class: progressFill,
                        style: `width: ${progressPercent(task)}%; background: ${statusColor(task.status)};`,
                      }),
                    ]))
                  }
                  return m('div', {
                    key: task.id,
                    class: taskCard,
                    onclick: () => m.route.set(`/projects/${projectId}/tasks/${task.id}`),
                  }, children)
                }),
              ),

        // --- Create task modal ---
        showForm
          ? m('div', { class: formOverlay, onclick: (e) => { if (e.target === e.currentTarget) closeForm() } }, [
              m('div', { class: formCard }, (() => {
                const fields = []

                fields.push(m('div', { key: 'title-heading', class: formTitle }, 'New task'))

                fields.push(m('div', { key: 'title', class: formRow }, [
                  m('label', { class: label }, 'Title'),
                  m('input', {
                    class: input,
                    value: formTitle,
                    oninput: (e) => { formTitle = e.target.value },
                    placeholder: 'e.g., Evaluate housing law sessions',
                    autofocus: true,
                  }),
                ]))

                fields.push(m('div', { key: 'desc', class: formRow }, [
                  m('label', { class: label }, 'Description'),
                  m('textarea', {
                    class: input,
                    style: 'min-height: 60px; resize: vertical;',
                    value: formDescription,
                    oninput: (e) => { formDescription = e.target.value },
                    placeholder: 'Instructions for the reviewer',
                  }),
                ]))

                fields.push(m('div', { key: 'type', class: formRow }, [
                  m('label', { class: label }, 'Task type'),
                  m('select', {
                    class: input,
                    value: formType,
                    onchange: (e) => { formType = e.target.value },
                  }, [
                    m('option', { value: 'tagging' }, 'Tagging'),
                    m('option', { value: 'evaluation' }, 'Evaluation'),
                    m('option', { value: 'review' }, 'Review'),
                  ]),
                ]))

                // Rubric picker (only for evaluation tasks)
                if (formType === 'evaluation' && rubricList.length > 0) {
                  const rubricChildren = [
                    m('label', { class: label }, 'Rubric'),
                    m('select', {
                      class: input,
                      value: formRubricId,
                      onchange: (e) => { formRubricId = e.target.value },
                    }, [
                      m('option', { value: '' }, 'No specific rubric'),
                      ...rubricList.map(r =>
                        m('option', { value: r.id }, r.title)
                      ),
                    ]),
                  ]
                  if (formRubricId) {
                    rubricChildren.push(
                      m('div', { style: `font-size: ${font.size.xs}; color: ${colors.textSecondary}; margin-top: 4px;` },
                        'This rubric will be automatically assigned to all selected sessions.')
                    )
                  }
                  fields.push(m('div', { key: 'rubric', class: formRow }, rubricChildren))
                }

                fields.push(m('div', { key: 'pii', class: formRow }, [
                  m('label', {
                    style: `display: flex; align-items: center; gap: ${space.sm}; cursor: pointer;`,
                  }, [
                    m('input', {
                      type: 'checkbox',
                      checked: formMaskPii,
                      onchange: (e) => { formMaskPii = e.target.checked },
                    }),
                    m('span', {
                      style: `font-size: ${font.size.sm}; color: ${colors.textSecondary};`,
                    }, 'Mask PII-highlighted text for guests'),
                  ]),
                ]))

                // --- Session filter section ---
                const filterChildren = [
                  m('label', { class: label }, 'Sessions'),

                  // Search bar
                  m('input', {
                    class: input,
                    style: `width: 100%; margin-bottom: ${space.sm}; box-sizing: border-box;`,
                    type: 'text',
                    placeholder: 'Search by session ID or message content...',
                    value: filterSearch,
                    oninput: (e) => { filterSearch = e.target.value; onFilterChange() },
                  }),
                ]

                // Filter controls row — build imperatively to avoid null holes
                const filterControls1 = []
                if (allTags.length > 0) filterControls1.push(renderTagFilter())
                filterControls1.push(m('span', { class: filterLabel }, 'Date:'))
                filterControls1.push(m('input', {
                  class: filterInput,
                  type: 'date',
                  value: filterDateFrom,
                  onchange: (e) => { filterDateFrom = e.target.value; onFilterChange() },
                }))
                filterControls1.push(m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, 'to'))
                filterControls1.push(m('input', {
                  class: filterInput,
                  type: 'date',
                  value: filterDateTo,
                  onchange: (e) => { filterDateTo = e.target.value; onFilterChange() },
                }))
                filterChildren.push(m('div', { class: filterRow }, filterControls1))

                const filterControls2 = [
                  m('span', { class: filterLabel }, 'Messages:'),
                  m('input', {
                    class: filterInput,
                    style: 'width: 70px;',
                    type: 'number',
                    placeholder: 'Min',
                    min: 0,
                    value: filterMinMessages,
                    onchange: (e) => { filterMinMessages = e.target.value; onFilterChange() },
                  }),
                  m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, '\u2013'),
                  m('input', {
                    class: filterInput,
                    style: 'width: 70px;',
                    type: 'number',
                    placeholder: 'Max',
                    min: 0,
                    value: filterMaxMessages,
                    onchange: (e) => { filterMaxMessages = e.target.value; onFilterChange() },
                  }),
                  m('div', { class: filterDivider }),
                  m('span', { class: filterLabel }, 'Evaluated:'),
                  m('select', {
                    class: filterSelect,
                    value: filterEvalStatus,
                    onchange: (e) => {
                      filterEvalStatus = e.target.value
                      if (filterEvalStatus === 'any') filterEvalRubricId = ''
                      onFilterChange()
                    },
                  }, [
                    m('option', { value: 'any' }, 'Any'),
                    m('option', { value: 'evaluated' }, 'Evaluated'),
                    m('option', { value: 'unevaluated' }, 'Not evaluated'),
                  ]),
                ]
                if (rubricList.length > 0 && filterEvalStatus !== 'any') {
                  filterControls2.push(m('select', {
                    class: filterSelect,
                    value: filterEvalRubricId,
                    onchange: (e) => { filterEvalRubricId = e.target.value; onFilterChange() },
                  }, [
                    m('option', { value: '' }, 'Any rubric'),
                    ...rubricList.map(r =>
                      m('option', { value: r.id }, r.title)
                    ),
                  ]))
                }
                filterChildren.push(m('div', { class: filterRow }, filterControls2))

                // Selected tag chips
                const tagChips = renderSelectedTagChips()
                if (tagChips) filterChildren.push(tagChips)

                // Match count preview
                filterChildren.push(m('div', { class: matchCount },
                  previewLoading
                    ? 'Counting...'
                    : previewCount !== null
                      ? `${previewCount.toLocaleString()} session${previewCount === 1 ? '' : 's'} match`
                      : 'All sessions',
                ))

                fields.push(m('div', { key: 'filters', class: formRow }, filterChildren))

                fields.push(m('div', { key: 'actions', class: formActions }, [
                  m('button', { class: btnSecondary, onclick: closeForm }, 'Cancel'),
                  m('button', {
                    class: btnPrimary,
                    disabled: saving || !formTitle.trim() || previewCount === 0 || previewLoading,
                    onclick: submitForm,
                  }, saving ? 'Creating...' : `Create task${previewCount != null ? ` (${previewCount.toLocaleString()} sessions)` : ''}`),
                ]))

                return fields
              })()),
            ])
          : null,
      ])
    },
  }
}
